import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, CalendarCheck, MapPinLine, Trophy, TrendUp, Warning, Sparkle, CurrencyInr } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_COLORS = ["#D4AF37", "#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#EC4899", "#22D3EE", "#EF4444"];

function MetricCard({ label, value, icon: Icon, hint, accent, testid, compact }) {
  return (
    <div className="card-luxury p-5 animate-fade-up" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className={compact ? "min-w-0" : ""}>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">{label}</div>
          <div className={`font-serif mt-2 text-white ${compact === "nowrap" ? "text-lg sm:text-3xl whitespace-nowrap" : compact ? "text-xl sm:text-3xl break-all" : "text-3xl"}`}>{value}</div>
          {hint && <div className={`text-xs mt-1 ${accent || "text-slate-400"}`}>{hint}</div>}
        </div>
        <div className="w-10 h-10 shrink-0 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37]">
          <Icon size={20} weight="duotone" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [team, setTeam] = useState([]);
  const [sales, setSales] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/sales").then(r => setSales(r.data)).catch(() => {});
    api.get("/dashboard/stats").then(r => setStats(r.data));
    api.get("/dashboard/team").then(r => setTeam(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-400">Loading…</div>;

  const statusData = Object.entries(stats.status_breakdown || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Overview</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">{isAdmin ? "Dashboard" : "Command Center"}</h1>
          {isAdmin && <div className="text-sm text-slate-400 mt-1" data-testid="dash-admin-subtitle">Complete business performance · All Executives</div>}
        </div>
        <Link to="/leads" className="text-xs uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2 rounded-md hover:bg-[#D4AF37]/10 transition-colors" data-testid="dash-view-leads">
          View all leads →
        </Link>
      </div>

      {isAdmin ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard label="Total Leads" value={stats.total_leads} icon={Users} hint="All team leads" testid="metric-total-leads" />
          <MetricCard label="Today's Follow-ups" value={stats.todays_followups} icon={CalendarCheck} hint={stats.overdue_followups > 0 ? `Across the team · ${stats.overdue_followups} overdue` : "Across the team"} accent={stats.overdue_followups > 0 ? "text-rose-400" : "text-emerald-400"} testid="metric-todays-followups" />
          <MetricCard label="Site Visits" value={stats.site_visits} icon={MapPinLine} hint="All team visits" testid="metric-site-visits" />
          <MetricCard label="Total Sales Value" value={`₹${Number(sales?.total_amount || 0).toLocaleString("en-IN")}`} icon={CurrencyInr} hint={`${sales?.total_deals || 0} completed deal${sales?.total_deals === 1 ? "" : "s"}`} accent="text-[#D4AF37]" testid="metric-sales" compact />
        </div>
      ) : (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Total Leads" value={stats.total_leads} icon={Users} testid="metric-total-leads" />
        <MetricCard label="Today's Follow-ups" value={stats.todays_followups} icon={CalendarCheck} hint={stats.overdue_followups > 0 ? `${stats.overdue_followups} overdue` : "All on track"} accent={stats.overdue_followups > 0 ? "text-rose-400" : "text-emerald-400"} testid="metric-todays-followups" />
        <MetricCard label="Site Visits" value={stats.site_visits} icon={MapPinLine} testid="metric-site-visits" />
        <MetricCard label="Sales" value={`₹${Number(sales?.total_amount || 0).toLocaleString("en-IN")}`} icon={CurrencyInr} hint={`${sales?.total_deals || 0} deal${sales?.total_deals === 1 ? "" : "s"} closed`} accent="text-[#D4AF37]" testid="metric-sales" compact="nowrap" />
      </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-12 card-luxury p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400">Pipeline</div>
              <h3 className="font-serif text-xl text-white mt-1">Lead Status Distribution</h3>
            </div>
            <TrendUp className="text-[#D4AF37]" size={22} weight="duotone" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData}>
                <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={{ stroke: "#1E293B" }} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 11 }} axisLine={{ stroke: "#1E293B" }} />
                <Tooltip contentStyle={{ background: "#0A1120", border: "1px solid #1E293B", borderRadius: 8, color: "#fff" }} />
                <Bar dataKey="value" fill={isAdmin ? "#A7E6B5" : "#D4AF37"} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isAdmin && (
        <div className="card-luxury p-6" data-testid="team-perf">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="text-[#D4AF37]" size={20} weight="duotone" />
            <h3 className="font-serif text-xl text-white">Team Performance</h3>
          </div>
          <div className="space-y-3">
            {team.length === 0 && <div className="text-slate-500 text-sm">No team data yet.</div>}
            {team.map((t, i) => (
              <div key={t.user.id} className="flex items-center justify-between p-3 rounded-md bg-[#060B14] border border-[#1E293B]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#1E293B] text-[#D4AF37] flex items-center justify-center font-semibold text-sm border border-[#D4AF37]/20">
                    {t.user.name.charAt(0)}
                  </div>
                  <div>
                    <div className="text-sm text-white">{t.user.name}</div>
                    <div className="text-xs text-slate-400 uppercase tracking-widest">{t.user.role}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-white font-semibold">{t.total}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">Leads</div>
                  </div>
                  <div className="text-center">
                    <div className="text-[#D4AF37] font-semibold">{t.closed}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">Closed</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        <div className={`card-luxury p-6 relative overflow-hidden ${isAdmin ? "" : "lg:col-span-2"}`}>
          <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkle className="text-[#D4AF37] pulse-gold rounded-full" size={22} weight="duotone" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">AI Insights</div>
            </div>
            <h3 className="font-serif text-2xl text-white mt-1">Talk to Nawab AI</h3>
            <p className="text-slate-300 text-sm mt-2 leading-relaxed">
              Get instant lead scoring, follow-up drafts, objection handlers, and market briefs — powered by Claude Sonnet 4.5.
            </p>
            <div className="mt-4 flex gap-2">
              <Link to="/ai-assistant" data-testid="dash-open-ai" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] text-sm font-semibold hover:bg-[#F2CE6E] transition-colors">
                Open Assistant
              </Link>
              <Link to="/leads" className="px-4 py-2 rounded-md border border-[#D4AF37]/30 text-[#D4AF37] text-sm hover:bg-[#D4AF37]/10 transition-colors">
                Score a lead
              </Link>
            </div>
          </div>
        </div>
      </div>

      {stats.overdue_followups > 0 && (
        <div className="p-4 rounded-md border border-rose-400/30 bg-rose-400/5 flex items-center gap-3">
          <Warning className="text-rose-400" size={20} weight="duotone" />
          <div className="text-sm text-rose-200">
            <b>{stats.overdue_followups}</b> follow-up{stats.overdue_followups > 1 ? "s are" : " is"} overdue. <Link to="/followups" className="underline">Review now →</Link>
          </div>
        </div>
      )}
    </div>
  );
}
